/**
 * useInvestment Hook
 *
 * React Query hooks for single investment operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  investmentsService,
  CreateInvestmentDto,
  UpdateInvestmentDto,
  InvestmentSummary,
} from '../services/investments.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

import type { Investment } from '@medsuite/models';

/**
 * Fetch single investment by ID
 */
export function useInvestment(
  id: number,
  options?: Omit<UseQueryOptions<Investment, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Investment, Error>({
    queryKey: investmentKeys.detail(id),
    queryFn: async () => {
      const response = await investmentsService.getById(id);
      // Extract data from success wrapper: {success: true, data: {...}, meta: {}}
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Fetch investment summary with aggregated data
 */
export function useInvestmentSummary(
  id: number,
  options?: Omit<UseQueryOptions<InvestmentSummary, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentSummary, Error>({
    queryKey: investmentKeys.summary(id),
    queryFn: async () => {
      const response = await investmentsService.getSummary(id);
      // Extract data from success wrapper: {success: true, data: {...}, meta: {}}
      return unwrapResponse(response.data);
    },
    ...options,
  });
}

/**
 * Create new investment
 */
export function useCreateInvestment(
  options?: UseMutationOptions<Investment, Error, CreateInvestmentDto>
) {
  const queryClient = useQueryClient();

  return useMutation<Investment, Error, CreateInvestmentDto>({
    mutationFn: async (data) => {
      const response = await investmentsService.create(data);
      // Extract data from success wrapper: {success: true, data: {...}, meta: {}}
      return unwrapResponse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Update investment
 */
export function useUpdateInvestment(
  options?: UseMutationOptions<Investment, Error, { id: number; data: UpdateInvestmentDto }>
) {
  const queryClient = useQueryClient();

  return useMutation<Investment, Error, { id: number; data: UpdateInvestmentDto }>({
    mutationFn: async ({ id, data }) => {
      const response = await investmentsService.update(id, data);
      // Extract data from success wrapper: {success: true, data: {...}, meta: {}}
      return unwrapResponse(response.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: investmentKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(variables.id) });
    },
    ...options,
  });
}

/**
 * Delete investment
 */
export function useDeleteInvestment(options?: UseMutationOptions<void, Error, number>) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await investmentsService.delete(id);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
      queryClient.removeQueries({ queryKey: investmentKeys.detail(id) });
    },
    ...options,
  });
}

/**
 * Update score override
 */
export function useUpdateScoreOverride(
  options?: UseMutationOptions<Investment, Error, { id: number; scoreOverride: number | null }>
) {
  const queryClient = useQueryClient();

  return useMutation<Investment, Error, { id: number; scoreOverride: number | null }>({
    mutationFn: async ({ id, scoreOverride }) => {
      const response = await investmentsService.updateScoreOverride(id, scoreOverride);
      // Extract data from success wrapper: {success: true, data: {...}, meta: {}}
      return unwrapResponse(response.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: investmentKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}
